/**
 * The agent that works on a value, for one item: which agent it is (the value's
 * own AI setting, migration 920), whether it can run, and the acts on it — ask
 * for a recommendation, accept one, reject one. The item page's closed Assessment
 * row and the value card it opens both use this, so there is one copy of each rule.
 *
 * Owner 2026-10-21: the agent is on the closed row, and a person may ask it even
 * when the value already has an official value — the answer waits beside it as a
 * recommendation; nothing changes until a person accepts it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTemplateAgents } from './useTemplateAgents';
import { useAiControl } from '../lib/ai/aiAvailability';
import { agentReviewerAllowed } from '../lib/agentRuntimeService';
import {
  agentProposerId,
  estimateValueForItem,
  isAgentValueProposer,
  type EstimateOutcome,
} from '../lib/valueEstimatorRunner';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../lib/supabaseSync';
import { useValueAiSettingsStore } from '../stores/prioritization/valueAiSettingsStore';
import { useValueProposalStore } from '../stores/prioritization/valueProposalStore';
import { useUserStore } from '../stores/prioritization/userStore';

export interface AiEstimator {
  agentType: string;
  name: string;
  onEstimate: (attrKey: string) => Promise<EstimateOutcome>;
}

/** Where a value's agent stands, for drawing it. */
export type ValueAgentState =
  /** No agent works on this value (its AI setting is off), or the agent is set not to show on the item. */
  | { kind: 'none' }
  /** An agent works on it but cannot run now; the reason is said, no button is drawn. */
  | { kind: 'cannot_run'; name: string; reason: string }
  /** An agent works on it but no AI model answers: the shared no-key line (AskAiNeedsKey) is drawn, no button. */
  | { kind: 'needs_key'; name: string }
  /** Whether a model answers is still being checked: nothing is drawn yet, so nothing flashes. */
  | { kind: 'checking' }
  /** The agent can be asked. */
  | { kind: 'ready'; estimator: AiEstimator };

export function useValueAgents(itemId: string) {
  const userId = useUserStore((s) => s.userId) || '';
  const currentRole = useUserStore((s) => s.currentRole) || 'workspace-member';
  const { acceptProposal, rejectProposal, fetchFromSupabase } = useValueProposalStore();
  // The one draw rule for every AI control (useAiControl): known to answer, or not.
  const { drawable: aiDrawable, needsKey: aiNeedsKey } = useAiControl(true);

  // Estimating agents: the Value Estimator and custom agents on its behavior, each with its settings.
  const estimateAgents = useTemplateAgents('estimate_value');
  // An agent set not to show on the item (spec §2 F) keeps its button and its proposals to its report page.
  const shownOnItem = useCallback(
    (settings: (typeof estimateAgents)[number]['settings']) =>
      (settings?.delivery as { surfaceOnObject?: boolean } | undefined)?.surfaceOnObject !== false,
    [],
  );
  const hiddenProposers = useMemo(
    () => new Set(estimateAgents.filter(({ settings }) => !shownOnItem(settings)).map(({ def }) => agentProposerId(def.agentType))),
    [estimateAgents, shownOnItem],
  );

  // Reviewer rights per agent, asked once; the database enforces the same rule.
  const [reviewRights, setReviewRights] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const ws = getCurrentWorkspaceId();
    if (!ws || !userId) return;
    let cancelled = false;
    void Promise.all(estimateAgents.map(async ({ def }) => [agentProposerId(def.agentType), await agentReviewerAllowed(ws, def.agentType, userId)] as const))
      .then((pairs) => { if (!cancelled) setReviewRights(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [estimateAgents, userId]);
  const mayReview = useCallback((proposedBy: string) => !isAgentValueProposer(proposedBy) || reviewRights[proposedBy] === true, [reviewRights]);

  const valueAiSettings = useValueAiSettingsStore((st) => st.settings);
  const loadValueAi = useValueAiSettingsStore((st) => st.load);
  useEffect(() => { const ws = getCurrentWorkspaceId(); if (ws) void loadValueAi(ws); }, [loadValueAi]);
  const valueAiSettingFor = useCallback((key: string) => valueAiSettings.find((v) => v.attributeKey === key) ?? null, [valueAiSettings]);

  const agentFor = useCallback(
    (attributeKey: string): ValueAgentState => {
      // Which agent works on a value is the value's own AI setting (migration 920).
      const v = valueAiSettingFor(attributeKey);
      if (!v || v.mode === 'off') return { kind: 'none' };
      const found = estimateAgents.find(({ def }) => def.agentType === v.agentType);
      if (!found) return { kind: 'none' };
      const { def, settings } = found;
      if (!shownOnItem(settings)) return { kind: 'none' };
      if (!settings?.enabled) return { kind: 'cannot_run', name: def.name, reason: `${def.name} is off. An admin turns it on in Settings → Agents.` };
      if ((settings.triggers as { manualSingle?: boolean } | undefined)?.manualSingle === false) {
        return { kind: 'cannot_run', name: def.name, reason: `${def.name} is set not to be asked about one item at a time.` };
      }
      if (aiNeedsKey) return { kind: 'needs_key', name: def.name };
      if (!aiDrawable) return { kind: 'checking' };
      return {
        kind: 'ready',
        estimator: {
          agentType: def.agentType,
          name: def.name,
          onEstimate: async (key: string): Promise<EstimateOutcome> => {
            const ws = getCurrentWorkspaceId();
            if (!ws) return { outcome: 'error', reason: 'No workspace selected.' };
            const outcome = await estimateValueForItem({
              agent: { agentType: def.agentType, name: def.name },
              workspaceId: ws,
              itemId,
              attributeKey: key,
              runByUserId: userId,
              settings,
              askAgain: true,
            });
            if (outcome.outcome === 'proposed') await fetchFromSupabase();
            return outcome;
          },
        },
      };
    },
    [valueAiSettingFor, estimateAgents, shownOnItem, aiDrawable, aiNeedsKey, itemId, userId, fetchFromSupabase],
  );

  /** The agents a value card offers: the one its AI setting names, when it can run. */
  const estimatorsFor = useCallback(
    (attributeKey: string): AiEstimator[] => {
      const a = agentFor(attributeKey);
      return a.kind === 'ready' ? [a.estimator] : [];
    },
    [agentFor],
  );

  /** Accept a recommendation as it is. Throws with a readable message. */
  const acceptRecommendation = useCallback(
    async (proposalId: string) => {
      // Prefer the Supabase auth user so accepted_by matches who is actually signed in
      // (the demo user picker can diverge from the session).
      let acceptorId = userId;
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) acceptorId = session.user.id;
      }
      await acceptProposal({ proposalId, acceptedBy: acceptorId, acceptorRole: currentRole, acceptanceMethod: 'facilitator_override' });
      await fetchFromSupabase();
    },
    [acceptProposal, userId, currentRole, fetchFromSupabase],
  );

  /** Reject a recommendation with the person's reason. Throws with a readable message. */
  const rejectRecommendation = useCallback(
    async (proposalId: string, reason: string) => {
      await rejectProposal({ proposalId, rejectedBy: userId, reason: reason.trim() || 'Rejected from item Values tab' });
      await fetchFromSupabase();
    },
    [rejectProposal, userId, fetchFromSupabase],
  );

  return { estimateAgents, hiddenProposers, mayReview, valueAiSettingFor, agentFor, estimatorsFor, acceptRecommendation, rejectRecommendation };
}
